import React, { useCallback, useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
    Box,
    Typography,
    LinearProgress,
    Button,
    Breadcrumbs,
    Link as MuiLink,
    Chip,
    AppBar,
} from '@mui/material';
import { useAppDispatch } from '../../app/hooks';
import {
    BackgroundProcessSummaryDto,
    loadAllBackgroundProcessSummaryForObject,
} from './backgroundProcessingApi';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { Localized } from '@fluent/react';

export const BackgroundProcessList: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { objectType = '', objectId = '' } = useParams();

    const [processesList, setProcessesList] = useState<BackgroundProcessSummaryDto[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadAllBackgroundProcessSummaryForObject({ objectType, objectId }))
            .then(unwrapResult)
            .then(setProcessesList)
            .finally(() => setIsLoading(false));
    }, []);


    const handleBackButtonClick = useCallback(() => {
        navigate(-1)
    }, [navigate])
    
    // 🎨 Status color mapping
    const getStatusColor = (status: string): string => {
        switch (status.toLowerCase()) {
            case "completed":
            case "success":
                return "#2e7d32"; // green
            case "inprogress":
            case "pending":
                return "#0288d1"; // blue
            case "failed":
            case "error":
                return "#d32f2f"; // red
            default:
                return "inherit";
        }
    };


    const columns: GridColDef[] = [
        {
            field: 'processNumber',
            headerName: 'Process No.',
            width: 130,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'operationType',
            headerName: 'Operation Type',
            width: 160,
        },
        {
            field: 'initiatedByUserId',
            headerName: 'Ordered By',
            width: 180
        },
        {
            field: 'startedAt',
            headerName: 'Started At',
            width: 180,
            valueGetter: (_, row) => {
                return row.startedAt ? new Date(row.startedAt).toISOString().split('T')[0] : "";
            }
        },
        {
            field: 'completedAt',
            headerName: 'Completed At',
            width: 180,
            valueGetter: (_, row) => {
                return row.completedAt ? new Date(row.completedAt).toISOString().split('T')[0] : "";
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    sx={{
                        backgroundColor: getStatusColor(params.value),
                        color: '#fff',
                        fontWeight: 'bold',
                        px: 1,
                    }}
                />
            )
        },
        {
            field: 'progressPercent',
            headerName: 'Progress',
            flex: 1,
            sortable: false,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box width="100%" textAlign="center">
                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                        {params.value}%
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={params.value}
                        sx={{
                            height: 10,
                            borderRadius: 2,
                            [`& .MuiLinearProgress-bar`]: {
                                backgroundColor: getStatusColor(params.row.status),
                            },
                        }}
                    />
                </Box>
            )

        },
        {
            field: 'resultUrl',
            headerName: 'Report',
            width: 160,
            renderCell: (params) => (
                <Button
                    onClick={() => navigate(`/matching-results/${params.row.id}`)}
                    size="small"
                    variant="outlined"
                >
                    View Report
                </Button>
            ),
        },
    ];

    return (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>
                Background processes for object {objectType} #{objectId}
            </Typography>

            <DataGrid
                autoHeight
                loading={isLoading}
                rows={processesList}
                columns={columns}
                getRowId={(row) => row.id}
                disableColumnMenu
                disableRowSelectionOnClick
            />
            <AppBar position="fixed" color="transparent" sx={{ top: 'auto', bottom: 0, p: 0.5 }}>
                <div className="flex justify-end">

                    <Button variant='contained' color="primary" size="small" onClick={handleBackButtonClick}>
                        <Localized id='back'>Wróć</Localized>
                    </Button>
                </div>
            </AppBar>
        </Box>
    );
};
